/**
 * FilterBuilder Component
 *
 * Container for filter rows with "Add Filter" button.
 * Manages the list of filter conditions.
 */

import { Button, Space } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useEffect } from 'react'
import { FilterRow } from './FilterRow'
import { getDefaultOperator } from '../operatorConfig'
import type { FilterBuilderProps, FilterRow as FilterRowType } from '../advancedFiltersTypes'

export function FilterBuilder({ model, fields, filters, onChange, onAddFilterRef, baseFilters, hasLifecycle }: FilterBuilderProps) {
  // Add new filter row
  const handleAddFilter = () => {
    const defaultOperator = getDefaultOperator('string') // Default to string operators

    const newFilter: FilterRowType = {
      id: `filter-${Date.now()}-${Math.random()}`,
      field: '',
      operator: defaultOperator.value,
    }

    onChange([...filters, newFilter])
  }

  // Expose handleAddFilter via ref for keyboard shortcuts
  useEffect(() => {
    if (onAddFilterRef) {
      onAddFilterRef.current = handleAddFilter
    }
  }, [onAddFilterRef])

  // Update a filter row
  const handleFilterChange = (index: number, updatedFilter: FilterRowType) => {
    const newFilters = [...filters]
    newFilters[index] = updatedFilter
    onChange(newFilters)
  }

  // Remove a filter row
  const handleRemoveFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index)
    onChange(newFilters)
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Filter Rows */}
      {filters.length > 0 && (
        <div>
          {filters.map((filter, index) => (
            <FilterRow
              key={filter.id}
              filter={filter}
              model={model}
              fields={fields}
              onChange={(updated) => handleFilterChange(index, updated)}
              onRemove={() => handleRemoveFilter(index)}
              baseFilters={baseFilters}
              hasLifecycle={hasLifecycle}
            />
          ))}
        </div>
      )}

      {/* Add Filter Button */}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAddFilter}
        block
        style={{ marginTop: filters.length > 0 ? 8 : 0 }}
      >
        Add Filter
      </Button>
    </Space>
  )
}
